import {
  Button,
  DismissIcon,
  Modal,
  Tag,
  Typography,
  useDrawerContext,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useId } from 'react';

import { BottomSheet } from '../bottomSheet/bottomSheet';

import styles from './featureIntroModal.module.css';

export interface FeatureIntroModalProps {
  actionLabel: string;
  /** Shown in the grey panel under the title, e.g. a working demo of the feature and a line about it. */
  children: ReactNode;
  /** Bottom sheet on mobile, centered dialog otherwise. */
  isMobile: boolean;
  /** Called before the modal closes itself. */
  onAction: () => void;
  /** Called once it has closed (X, Esc, the overlay, or the action), after the sheet's slide-out on mobile. */
  onRequestClose: () => void;
  open: boolean;
  testId?: string;
  title: string;
}

interface ContentProps extends Omit<FeatureIntroModalProps, 'open' | 'testId'> {
  titleId: string;
}

function FeatureIntroContent({
  actionLabel,
  children,
  isMobile,
  onAction,
  onRequestClose,
  title,
  titleId,
}: ContentProps) {
  return (
    <div className={clsx(styles['content'], isMobile && styles['mobile'])}>
      <Button.Icon
        className={styles['close']}
        icon={<DismissIcon />}
        label="Close"
        onClick={onRequestClose}
      />
      <Tag background="success-light" color="success-dark" label="NEW!" />
      <Typography
        as="h2"
        branded
        className={styles['title']}
        id={titleId}
        textAlign="center"
        variant={isMobile ? 'h3-semibold' : 'h2-semibold'}
      >
        {title}
      </Typography>
      <div className={styles['panel']}>{children}</div>
      <Button
        className={styles['action']}
        full={isMobile}
        label={actionLabel}
        onClick={() => {
          onAction();
          onRequestClose();
        }}
      />
    </div>
  );
}

/** Closes through the sheet so it slides out like an overlay tap. */
function SheetContent(props: ContentProps) {
  const { onRequestClose } = useDrawerContext();

  return <FeatureIntroContent {...props} onRequestClose={onRequestClose} />;
}

/**
 * Announces a new feature: a NEW! tag, the title, a grey panel for the
 * caller's content, and a single action. The caller owns `open`; see
 * `useFeatureIntro` to show it once per shopper.
 */
export function FeatureIntroModal({
  isMobile,
  onRequestClose,
  open,
  testId = 'feature-intro-modal',
  ...contentProps
}: FeatureIntroModalProps) {
  const titleId = useId();
  const props = { ...contentProps, isMobile, onRequestClose, titleId };

  if (isMobile) {
    return (
      <BottomSheet
        aria-labelledby={titleId}
        data-testid={testId}
        onRequestClose={onRequestClose}
        open={open}
      >
        <SheetContent {...props} />
      </BottomSheet>
    );
  }

  return (
    <Modal
      ariaLabelledBy={titleId}
      background="grays-white"
      data-testid={testId}
      onRequestClose={onRequestClose}
      open={open}
      overlayClose
      variant="dialogue"
    >
      <FeatureIntroContent {...props} />
    </Modal>
  );
}
